'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import type { RefCallback } from 'react';
import IconExperimence from '@/components/assets/icon_experimence.svg';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import IconPlay from '@/components/assets/icon_play.svg';
import OptimizedImage from '@/components/OptimizedImage';
import PremiumBadge from '@/components/PremiumBadge';
import { WORLD_CARD_ADVERTISED_SRC_WIDTH } from '@/constants/card';
import { useWorldCardContext } from '@/context/ViverseWorldCardContext';
import { Link } from '@/i18n/navigation';
import { formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';
import roomDefaultImage from '@/public/images/room-default.png';
const HoverVideoPreview = dynamic(() => import('@/components/HoverVideoPreview'), {
  ssr: false
});
type ViverseWorldCardMediaProps = {
  isHovered: boolean;
  isHoverSupported: boolean;
  isDwellPlaying: boolean;
  cardRef: RefCallback<HTMLDivElement>;
};
export default function ViverseWorldCardMedia(t0) {
  const $ = _c(53);
  const {
    isHovered,
    isHoverSupported,
    isDwellPlaying,
    cardRef
  } = t0;
  const {
    targetUrl,
    imageAlt,
    cardType,
    resolvedImage,
    showPremiumTag,
    isClient,
    vodId,
    imageClassName,
    mediaClassName,
    imageSizes,
    priority,
    info: t1
  } = useWorldCardContext();
  const {
    is_live,
    vod_duration
  } = t1;
  const t2 = cardType === "videos" ? "relative aspect-320/180 w-full overflow-hidden rounded-3xl" : "relative aspect-430/242 w-full overflow-hidden rounded-3xl";
  let t3;
  if ($[0] !== mediaClassName || $[1] !== t2) {
    t3 = cn(t2, mediaClassName);
    $[0] = mediaClassName;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isDwellPlaying || $[4] !== isHovered || $[5] !== vodId) {
    t4 = (isHovered || isDwellPlaying) && vodId && <div className="fade-in animate-in absolute inset-0 z-10 duration-300"><HoverVideoPreview vodId={vodId} /></div>;
    $[3] = isDwellPlaying;
    $[4] = isHovered;
    $[5] = vodId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = resolvedImage || roomDefaultImage.src;
  const t6 = isHoverSupported && "transition-all duration-500 group-hover:scale-115 group-hover:brightness-110";
  let t7;
  if ($[7] !== imageClassName || $[8] !== t6) {
    t7 = cn("select-none", t6, imageClassName);
    $[7] = imageClassName;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = imageSizes ?? "(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw";
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      objectFit: "cover"
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== priority) {
    t10 = priority && {
      fetchPriority: "high",
      loading: "eager"
    };
    $[11] = priority;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== imageAlt || $[14] !== t10 || $[15] !== t5 || $[16] !== t7 || $[17] !== t8) {
    t11 = <OptimizedImage src={t5} overrideSrcWidth={WORLD_CARD_ADVERTISED_SRC_WIDTH} className={t7} fill={true} sizes={t8} alt={imageAlt} style={t9} draggable={false} {...t10} />;
    $[13] = imageAlt;
    $[14] = t10;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== cardRef || $[20] !== t11 || $[21] !== t4) {
    t12 = <div ref={cardRef} className="relative size-full">{t4}{t11}</div>;
    $[19] = cardRef;
    $[20] = t11;
    $[21] = t4;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== targetUrl) {
    t13 = <Link href={targetUrl} prefetch={false} className="block size-full cursor-pointer" draggable={false}>{t12}</Link>;
    $[23] = t12;
    $[24] = targetUrl;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== isClient || $[27] !== is_live || $[28] !== targetUrl) {
    t14 = is_live && isClient && <div className="absolute top-3 left-3"><Link href={targetUrl} prefetch={false} className="cursor-pointer" draggable={false}><div className="caption flex items-center gap-2 rounded-full bg-[#00000099] px-2 text-white"><span className="bg-vive-live size-2 rounded-full" />Live</div></Link></div>;
    $[26] = isClient;
    $[27] = is_live;
    $[28] = targetUrl;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== showPremiumTag) {
    t15 = showPremiumTag && <div className="absolute top-1.5 right-2"><PremiumBadge showLabel={true} /></div>;
    $[30] = showPremiumTag;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="z-raised pointer-events-none absolute inset-0 hidden bg-linear-to-b from-transparent from-50% to-[rgba(8,8,10,0.2)] opacity-0 transition-opacity duration-300 lg:block lg:group-hover:opacity-100" />;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== cardType || $[34] !== isDwellPlaying || $[35] !== isHovered) {
    t17 = cardType === "videos" && <div className={cn("pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ease-out", isHovered || isDwellPlaying ? "opacity-0" : "opacity-100")}><div className="flex items-center justify-center rounded-full bg-white p-4 drop-shadow-[0px_0px_6px_rgba(0,0,0,0.55)]"><IconPlay className="size-[15px] fill-black" /></div></div>;
    $[33] = cardType;
    $[34] = isDwellPlaying;
    $[35] = isHovered;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== cardType || $[38] !== vod_duration) {
    t18 = vod_duration !== undefined && cardType === "videos" && <div className="pointer-events-none absolute right-2 bottom-2 rounded-full bg-[#121216] px-2 py-0.5 text-[12px] font-medium text-[#d5e2f2] backdrop-blur-md">{formatDuration(vod_duration)}</div>;
    $[37] = cardType;
    $[38] = vod_duration;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== cardType) {
    t19 = cardType === "experiences" && <div className="absolute right-0 bottom-0 rounded-tl-2xl bg-linear-to-b from-[#ff7cff] to-[#903bff] p-1.5"><IconExperimence className="size-6 fill-white" /></div>;
    $[40] = cardType;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== cardType) {
    t20 = cardType === "games" && <div className="absolute right-0 bottom-0 rounded-tl-2xl bg-linear-to-b from-[#33c2e9] to-[#3478f6] p-1.5"><IconGamepad className="size-6 fill-white" /></div>;
    $[42] = cardType;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t13 || $[45] !== t14 || $[46] !== t15 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20 || $[51] !== t3) {
    t21 = <div className={t3}>{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}</div>;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t3;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  return t21;
}